import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Autocomplete,
  Breadcrumb,
  Breadcrumbs,
  Button,
  Card,
  Typography,
  useAutocomplete
} from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { onGetBuyersThunk } from '../BuyersList/BuyersList.actions'
import { onCompareProvidersThunk } from '../Comparator/Comparator.actions'
import { onGetComparedBuyersThunk } from './CompareBuyersSection.actions'
import Jumbotron from '../../components/Basics/Jumbotron'
import { Column, Content, Description, Root, Row, Title } from './CompareBuyersSection.styles'

/**
 * The CompareBuyersSection's container.
 */
const CompareBuyersSection = () => {
  const dispatch = useDispatch()
  const {
    data: { firstEntity, secondEntity },
    fetching
  } = useSelector(state => state.comparator)
  const [firstBuyer, setFirstBuyer] = useState(null)
  const [secondBuyer, setSecondBuyer] = useState(null)
  const { data } = useSelector(state => state.buyersList)
  const { firstBuyers, secondBuyers } = useSelector(state => state.compareBuyersSection)
  const firstOptions =
    firstBuyers.length >= 1 ? firstBuyers : data.map(provider => ({ name: provider[0], value: provider[2] }))
  const secondOptions =
    secondBuyers.length >= 1 ? secondBuyers : data.map(provider => ({ name: provider[0], value: provider[2] }))
  const {
    option: firstOption,
    value: firstValue,
    onChange: firstChange,
    onSelect: firstSelect
  } = useAutocomplete({
    initialOption: firstEntity || null,
    initialValue: firstEntity?.name || '',
    changeCallback: updatedValue => {
      if (updatedValue.length < 3) {
        return
      }

      dispatch(onGetComparedBuyersThunk(updatedValue, 1))
    },
    selectCallback: updatedValue => {
      updatedValue !== null ? setFirstBuyer(updatedValue) : setFirstBuyer(null)
    }
  })
  const {
    option: secondOption,
    value: secondValue,
    onChange: secondChange,
    onSelect: secondSelect
  } = useAutocomplete({
    changeCallback: updatedValue => {
      if (updatedValue.length < 3) {
        return
      }

      dispatch(onGetComparedBuyersThunk(updatedValue, 2))
    },
    selectCallback: updatedValue => {
      updatedValue !== null ? setSecondBuyer(updatedValue) : setSecondBuyer(null)
    }
  })
  const compareProviders = () =>
    dispatch(onCompareProvidersThunk({ firstEntity: firstBuyer, secondEntity: secondBuyer }))

  useEffect(() => {
    dispatch(onGetBuyersThunk())

    if (!firstEntity) {
      return
    }

    setFirstBuyer(firstEntity)
  }, [firstEntity, secondEntity])

  return (
    <Root>
      <Row>
        <Column xs={12}>
          <Jumbotron>
            <Breadcrumbs>
              <Breadcrumb to={routes.root} variant="link">
                Datos Abiertos
              </Breadcrumb>
              <Breadcrumb>Organismos Compradores</Breadcrumb>
            </Breadcrumbs>
            <Title>Comparador</Title>
            <Description>
              El comparador te permite conocer las distintas cifras obtenidas a partir de las compras realizadas por dos
              <br /> organismos compradores a través de Mercado Público, ya sean montos transados, mecanismos de compra
              <br /> utilizados, licitaciones adjudicadas, rubros más transados y tipos de proveedores de los organismos
              compradores.
            </Description>
          </Jumbotron>
        </Column>
      </Row>
      <Content>
        <Row justifyContent="center" margin="48px 0 0">
          <Column md={8} xs={12}>
            <Typography fontSize="16px" fontWeight="bold" variant="body1">
              Selecciona los dos organismos compradores que deseas comparar:
            </Typography>
          </Column>
        </Row>
        <Row justifyContent="center" margin="24px 0 0">
          <Column md={8} xs={12}>
            <Row justifyContent="space-between" spacing={2}>
              <Column md={6} padding="0 12px" xs={12}>
                <Card color="blank" elevation="1">
                  <Autocomplete
                    label="Organismo N°1"
                    option={firstOption}
                    options={firstOptions.filter(option => option.value !== secondOption?.value)}
                    value={firstValue}
                    onChange={firstChange}
                    onSelect={firstSelect}
                  />
                </Card>
              </Column>
              <Column md={6} padding="0 12px" xs={12}>
                <Card color="blank" elevation="1">
                  <Autocomplete
                    label="Organismo N°2"
                    option={secondOption}
                    options={secondOptions.filter(option => option.value !== firstOption?.value)}
                    value={secondValue}
                    onChange={secondChange}
                    onSelect={secondSelect}
                  />
                </Card>
              </Column>
            </Row>
          </Column>
        </Row>
        <Row justifyContent="center" margin="48px 0 0">
          <Column align="center" md={3} xs={12}>
            <Button
              color="primary"
              disabled={(firstBuyer && secondBuyer) === null}
              loading={fetching}
              width="155px"
              onClick={compareProviders}
            >
              Comparar
            </Button>
          </Column>
        </Row>
      </Content>
    </Root>
  )
}

export default CompareBuyersSection
